import { utils } from '@digital-commerce/shared-utils'
import {
  allPass,
  equals,
  filter,
  ifElse,
  includes,
  isEmpty,
  isNil,
  not,
  path,
  pathEq,
  pathOr,
  pipe,
  prop,
  propEq,
  propOr,
  memoizeWith
} from 'ramda'

import {
  getFinancedAccessoryGroups,
  getFinancedAccessoryPenalties,
  getOneTimeFinancedAmountCredit,
  isPhone,
  productDetailsFactory
} from '../../../utils/mobility'
import { getColourAttributeText } from '../../../utils/display'
import { formatCurrency } from '../../../utils/currency'
import { getContractLength, getCacheKey } from '../../../utils/cart'

const { getItemOneTimePrice, getMonthlyItemPrice, getTopLevelItems } = utils.cart

export const getMobilityAccessories = memoizeWith(
  getCacheKey('MOB-Accessory'),
  pipe(getTopLevelItems, filter(pathEq(['category'], 'MOBILITY_ACCESSORY')))
)

const isPrepaid = pipe(prop('tags'), includes('prepaid'))
const isSim = pipe(prop('tags'), includes('sim'))

export const getSubHeading = ifElse(
  allPass([isPrepaid, pipe(isSim, not)]),
  () => 'prepaid',
  () => 'accessories'
)

const isAFinancedAccessory = (offer) => {
  const isAnAccessory = propEq('category', 'MOBILITY_ACCESSORY')
  const hasRecurringPrice = pipe(pathOr([], ['itemPrice', 'recurring']), isEmpty, not)
  return allPass([isAnAccessory, hasRecurringPrice])(offer)
}

export const calculateDueTodaySubTotal = (mobAccessory) =>
  getItemOneTimePrice(mobAccessory).subTotal -
  (isAFinancedAccessory(mobAccessory) ? getItemOneTimePrice(mobAccessory).credit : 0)

export const getAttributes = (mobAccessory, translate) => {
  const appendedAttributes = []

  const isPhoneItem = isPhone(mobAccessory)
  const shouldShowColour = allPass([pipe(equals('None'), not), pipe(isNil, not)])

  const size = pipe(propOr({}, 'content'), path(['size', 'fields', 'size']))(mobAccessory)
  const { colour } = productDetailsFactory(mobAccessory)

  const sizeAttribute = `${translate('size')}: ${size}`
  const colourAttribute = getColourAttributeText(mobAccessory, translate)

  // Phone attributes
  if (isPhoneItem) {
    if (colour && !size) {
      appendedAttributes.push(colourAttribute)
    }
    if (colour && size) {
      const colourAndSizeAttribute = `${colourAttribute}, ${sizeAttribute}`
      appendedAttributes.push(colourAndSizeAttribute)
    }
  }

  // Non-phone attributes
  if (!isPhoneItem) {
    if (shouldShowColour(colour)) {
      appendedAttributes.push(colourAttribute)
    }
    if (size) {
      appendedAttributes.push(sizeAttribute)
    }
  }

  return appendedAttributes
}

export const buildOneTimeDiscount = (mobAccessory, translate, lang) => {
  const { discount } = getItemOneTimePrice(mobAccessory) || {}

  if (!discount) return null

  return {
    id: `${mobAccessory.id}-one-time-discount`,
    name: translate('discount'),
    price: `-${formatCurrency(discount, lang)}`,
    prefixIcon: 'DEALS'
  }
}

export const buildOneTimeFinanceAmountCredit = (mobAccessory, translate, lang) => {
  const credit = getOneTimeFinancedAmountCredit(mobAccessory)

  if (!credit) return null

  const accessoryMonthlyPrice = getMonthlyItemPrice(mobAccessory)

  return {
    id: `${mobAccessory.id}-easy-payment-balance`,
    name: translate('item.easyPaymentBalance', {
      replacements: {
        duration: accessoryMonthlyPrice.duration,
        value: accessoryMonthlyPrice.amount
      }
    }),
    price: `-${formatCurrency(credit, lang)}`
  }
}

/**
 * Filters financed accessory groups without related offers
 * @param {array} groups
 * @returns {array} A list of financed accessory groups without associated offers
 */
const getGroupsWithoutOfferAssociation = (groups) => {
  return groups.filter((group) => {
    return group.financeDetails.some((detail) => detail.relatedItemId == null)
  })
}

/**
 * Gets all financed accessory groups from cart that are not linked to a parent item such as a phone
 * @param {object} cart
 * @returns {array} A list of financed accessory groups without associated offers
 */
export const getAFGroupsWithoutOfferAssociation = pipe(
  getFinancedAccessoryGroups,
  getGroupsWithoutOfferAssociation
)

export const buildAccessoryPenaltyCharges = (financedAccessoryGroup, translate, lang) => {
  const penalty = getFinancedAccessoryPenalties(financedAccessoryGroup)

  if (isEmpty(penalty)) return []

  return [
    {
      id: `${financedAccessoryGroup.id}-${penalty[0].name}`,
      name: translate('accessoryFinancing.PENALTY_AF'),
      price: formatCurrency(penalty[0].amount, lang)
    }
  ]
}

/**
 * Gets contract term label dependent if mob accessory is being purchased standalone or with a phone.
 *
 * Note: `buildContractTermLabelFn` is being passed in as a parameter to prevent dependency cycles (eslint issue)
 * @param {object} mobAccessory
 * @param {boolean} isPurchasedWithPhone
 * @param {Function} translate translate hook
 * @param {Function} buildContractTermLabelFn uses buildContractTermLabel fn from display.js
 * @returns
 */
export const getContractTermLabel = (
  mobAccessory,
  isPurchasedWithPhone,
  translate,
  buildContractTermLabelFn
) => {
  const monthlyTermLabel = {
    numberOfMonths: getContractLength(mobAccessory)
  }

  return isPurchasedWithPhone
    ? null
    : buildContractTermLabelFn(translate, mobAccessory, monthlyTermLabel)
}

export const shouldRender = (cart) => {
  const outrightPurchasedMobAccessories = getMobilityAccessories(cart)
  const financedAccessoryGroups = getAFGroupsWithoutOfferAssociation(cart)
  const shouldNotRender =
    (isNil(outrightPurchasedMobAccessories) || isEmpty(outrightPurchasedMobAccessories)) &&
    (isNil(financedAccessoryGroups) || isEmpty(financedAccessoryGroups))
  return !shouldNotRender
}
